/**
 * Route-level React error boundary.
 *
 * Wraps each top-level route so a crash in one route (e.g. AgenticNetworkRoom)
 * doesn't blank the whole app — the sidebar, auth state, and other routes stay
 * alive and navigable. Pair with React.lazy + <Suspense/>.
 *
 * Accepts a `routeName` string purely for telemetry/display; no production
 * side-effects here beyond console logging.
 */
import React from 'react';

export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    const name = this.props?.routeName || 'unknown-route';
    console.error(`[RouteErrorBoundary:${name}] crashed`, error, errorInfo);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const name = this.props?.routeName || 'route';
    const msg = error?.message || String(error);

    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 p-8 text-center">
        <div className="text-lg font-semibold text-gray-900">Something went wrong loading this page.</div>
        <div className="max-w-md text-sm text-gray-600">
          The <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">{name}</code> page failed to render.
          The rest of the app is still available — use the sidebar to navigate elsewhere.
        </div>
        <details className="mt-2 max-w-lg text-left text-xs text-gray-500">
          <summary className="cursor-pointer">Error details</summary>
          <pre className="mt-2 whitespace-pre-wrap break-words rounded bg-gray-50 p-2">{msg}</pre>
        </details>
        <button
          type="button"
          onClick={this.handleReset}
          className="mt-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Try again
        </button>
      </div>
    );
  }
}
